import { Flex, Typography } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { toCapitalize } from "../../shared";

dayjs.extend(utc);
dayjs.extend(timezone);

const START_HOUR = 0;
const END_HOUR = 23;
const ROW_HEIGHT = 64;
const EVENT_HEIGHT = 52;
const DEFAULT_DURATION_MIN = 60;

const { Paragraph } = Typography;

const statusColors = {
  Pending: "#FFA500",
  "Appointment scheduled": "#52c41a",
  Called: "#FFD700",
  "Callback required": "#722ed1",
  "No response": "#1677ff",
  "Not interested": "#f5222d",
};

const toLocal = (dateStr) => dayjs.utc(dateStr).local();

const formatHour = (h) => {
  const period = h < 12 ? "AM" : "PM";
  const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${String(display).padStart(2, "0")}:00 ${period}`;
};

const DraftCustomDayView = ({ date, events = [], onSelectEvent }) => {
  const hours = Array.from(
    { length: END_HOUR - START_HOUR + 1 },
    (_, i) => START_HOUR + i,
  );

  const V_GAP = 6;
  const EVENT_PADDING = 8;

  // ✅ Filter by local date using when_to_call_again or start
  const dayEvents = events.filter((e) => {
    const timeSource = e?.alldata?.when_to_call_again || e.start;
    return toLocal(timeSource).isSame(dayjs(date), "day");
  });

  // ✅ Group events by hour (same as CustomDayView)
  const eventsByHour = {};
  dayEvents.forEach((e) => {
    const timeSource = e?.alldata?.when_to_call_again || e.start;
    const h = toLocal(timeSource).hour();
    if (!eventsByHour[h]) eventsByHour[h] = [];
    eventsByHour[h].push(e);
  });

  // ✅ Dynamic row height based on count (same logic as CustomDayView)
  const getRowHeight = (hour) => {
    const count = eventsByHour[hour]?.length || 0;
    if (count <= 1) return ROW_HEIGHT;
    return count * EVENT_HEIGHT + (count - 1) * V_GAP + EVENT_PADDING * 2;
  };

  return (
    <div className="custom-day-view">
      {/* Header */}
      <div className="cdv-header">
        <div className="cdv-weekday">
          {dayjs(date).format("dddd").toUpperCase()}
        </div>
        <div className="cdv-date">{dayjs(date).format("MMMM D, YYYY")}</div>
        <div className="cdv-count">
          {dayEvents.length} Draft{dayEvents.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Timeline — flow layout, no fixed height, no absolute positioning */}
      <div
        className="cdv-timeline"
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {hours.map((h) => {
          const rowHeight = getRowHeight(h);
          const hourEvents = eventsByHour[h] || [];

          return (
            <div
              key={h}
              className="cdv-row"
              style={{
                position: "relative",
                height: rowHeight,
                minHeight: rowHeight,
                display: "flex",
                alignItems: "flex-start",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <div
                className="cdv-time"
                style={{
                  width: 70,
                  flexShrink: 0,
                  paddingTop: 6,
                }}
              >
                {formatHour(h)}
              </div>

              {/* Events container */}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: V_GAP,
                  padding: `${EVENT_PADDING}px 8px`,
                }}
              >
                {hourEvents.map((event) => {
                  const timeSource =
                    event?.alldata?.when_to_call_again || event.start;
                  const localTime = toLocal(timeSource).format("hh:mm A");
                  const bg = statusColors[event?.status] || "#888";

                  return (
                    <div
                      key={event.id}
                      className="cdv-event"
                      style={{
                        background: bg,
                        height: EVENT_HEIGHT,
                        position: "relative",
                        top: 0,
                        left: 0,
                        right: 0,
                        width: "100%",
                        overflow: "hidden",
                      }}
                      onClick={() => onSelectEvent?.(event)}
                    >
                      <Flex vertical gap={3} className="w-100">
                        <Flex
                          justify="space-between"
                          gap={10}
                          align="center"
                          className="w-100"
                        >
                          <Paragraph
                            className="m-0 fs-12 text-white"
                            ellipsis={{ rows: 1 }}
                            style={{
                              width: 100,
                              textAlign: "left",
                              display: "inline",
                            }}
                          >
                            {toCapitalize(event.title)}
                          </Paragraph>
                          <span style={{ color: "#fff", fontSize: 12 }}>
                            {localTime}
                          </span>
                        </Flex>
                        <Flex gap={2}>
                          <Paragraph
                            className="m-0 fs-10 text-white"
                            ellipsis={{ rows: 1 }}
                            style={{
                              width: "max-content",
                              textAlign: "left",
                              padding: "1px 7px",
                              borderRadius: 4,
                              border: "1px solid #d9d9d9",
                            }}
                          >
                            {toCapitalize(event?.alldata?.services?.[0]?.name)}
                          </Paragraph>
                          {event?.alldata?.services?.length > 1 && (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 500,
                                color: "#fff",
                                padding: "1px 7px",
                                borderRadius: 4,
                                border: "1px solid #d9d9d9",
                                whiteSpace: "nowrap",
                              }}
                            >
                              +{event?.alldata?.services?.length - 1}
                            </span>
                          )}
                        </Flex>
                      </Flex>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

DraftCustomDayView.range = (date) => [dayjs(date).startOf("day").toDate()];
DraftCustomDayView.navigate = (date, action) => {
  switch (action) {
    case "PREV":
      return dayjs(date).subtract(1, "day").toDate();
    case "NEXT":
      return dayjs(date).add(1, "day").toDate();
    default:
      return date;
  }
};
DraftCustomDayView.title = (date) => dayjs(date).format("MMMM D, YYYY");

export { DraftCustomDayView };
